// The twelve ship icon sets (R835, ui-map-interface.md §6.1).
//
// The `ship` field of a Marks body picks ONE set for the whole roster, and
// the fourteen hull fields `shipCorsair` to `shipSapper` override the set
// for one hull each. This file holds the geometry of every set and the map
// from a class to its hull field. The thirteenth hull is the Commodore, the
// command ship (R2805 retired its flag): its mark in each set is the
// pick of mockup round 108 (`archive/mockups/108-command-ship-marks.html`,
// R2550, v2-the-deep.md §16.7), in `COMMAND_EXTRAS` below. The fourteenth
// is the Sapper behind the flag `sapper`: its mark in each set is the pick
// of mockup round 067 (`archive/mockups/067-sapper-hull-marks.html`, R2538,
// R2652, v2-the-deep.md §11.8), in `SAPPER_EXTRAS` below.
//
// **The two bases are fixed.** Every combat mark is the dart of `marks.ts`,
// `poly([14,0, -8,5, -5,0, -8,-5])`, plus its extras. Every support mark is
// one needle, `needle(13, 1.6)`, plus its extras. A set states the EXTRAS
// alone, so no set can bend the rule.
//
// The sets come from mockup round thirty-seven
// (`archive/mockups/037-ship-symbols.html`), which pitched twenty rosters of
// the twelve hulls. The owner picked twelve of them:
//
// | set        | card | what the extras are                              |
// | ---------- | ---- | ------------------------------------------------ |
// | `default`  |   01 | the shipped marks: the WINGS family and the       |
// |            |      | round-six filigree on the one needle              |
// | `rungs`    |   02 | bars across the hull                             |
// | `pips`     |   03 | solid dots                                       |
// | `arcs`     |   04 | pieces of a circle                               |
// | `chevrons` |   05 | two bars meeting at a point                      |
// | `plates`   |   06 | a flat piece beside the hull                     |
// | `tails`    |   07 | what trails astern                               |
// | `teeth`    |   09 | small triangles on the flank                     |
// | `arrows`   |   10 | small darts and mini needles                     |
// | `by-name`  |   11 | the thing the class is named after                |
// | `insignia` |   14 | one small mark of the fewest strokes             |
// | `filigree` |   19 | hairlines on the combat row too                  |
//
// **The winding rule, and it is load-bearing.** The canvas and the atlas both
// fill with the nonzero rule, so two sub-paths that overlap and wind opposite
// ways CANCEL and leave a hole. `poly()` below therefore orients every polygon
// to a positive signed area, the orientation of an angle-increasing `arc` —
// which is what `circle()`, the outer circle of `ring()` and `band()` all use.
// `mirror()` negates y, which flips the orientation, and `poly()` puts it
// back. The `hole` option winds the other way on purpose; no set that is
// built uses it, and it stays for the symmetry of the primitive.
//
// **The rule of a description** (`marks.ts`): a path lays down paths and never
// calls `fill()` or `stroke()`. The master builder picks the ink, which is how
// one description gives both the filled master and the hollow one.
//
// This is a leaf: it imports no engine, and `hull-styles.test.ts` counts every
// shape it draws.

import { HULL_STYLES, SHIP_STYLES } from '../gen/balance';
import { ShipClass } from '../gen/proto/aquaphobia';
import {
  COMBAT_ICONS, SUPPORT_EXTRAS, SUPPORT_ICONS, band, dartPath, ring, starPoints,
  supportBase, type HullPath, type MarkGraphics,
} from './marks';
import type { SceneLook } from './scene-look';
import type { HullStyle, MarksHullField, ShipStyle } from '../ui/settings';
import { TAU } from './math';

const PI = Math.PI;

/** The AUTO ship icon set: the marks `marks.ts` draws (R822). */
export const DEFAULT_SHIP_STYLE: ShipStyle = SHIP_STYLES[0];
/** The AUTO value of a hull field: take the set (R835). */
export const FOLLOW_HULL_STYLE: HullStyle = HULL_STYLES[0];

// ---------------------------------------------------------------------------
// The path primitives of the mockup page
// ---------------------------------------------------------------------------

/** The shoelace sum of a closed polygon: twice its signed area. */
export function signedArea(p: readonly number[]): number {
  let a = 0;
  for (let i = 0; i < p.length; i += 2) {
    const j = (i + 2) % p.length;
    a += p[i] * p[j + 1] - p[j] * p[i + 1];
  }
  return a;
}

/**
 * A polygon, wound so it ADDS under the nonzero fill rule.
 *
 * The point order is reversed when the signed area has the wrong sign, so a
 * caller states the points in the order that reads best and the winding is
 * not its problem. `hole` asks for the other winding, which CUTS.
 */
export function poly(g: MarkGraphics, p: readonly number[], hole = false): void {
  if ((signedArea(p) > 0) !== hole) {
    g.poly([...p]);
    return;
  }
  const r: number[] = [];
  for (let i = p.length - 2; i >= 0; i -= 2) r.push(p[i], p[i + 1]);
  g.poly(r);
}

/** A straight bar of width `w` as one quad, wound to add. */
function bar(g: MarkGraphics, x0: number, y0: number, x1: number, y1: number, w: number): void {
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (w / 2), ny = (dx / len) * (w / 2);
  poly(g, [x0 + nx, y0 + ny, x1 + nx, y1 + ny, x1 - nx, y1 - ny, x0 - nx, y0 - ny]);
}

/** A solid dot. `circle()` winds the way `poly()` orients to. */
const dot = (g: MarkGraphics, x: number, y: number, r: number): void => { g.circle(x, y, r); };

/** An axis-aligned rectangle from two corners. */
function rect(g: MarkGraphics, x0: number, y0: number, x1: number, y1: number): void {
  poly(g, [x0, y0, x1, y0, x1, y1, x0, y1]);
}

/** The outline of a rectangle, as four bars. */
function box(
  g: MarkGraphics, x0: number, y0: number, x1: number, y1: number, w: number,
): void {
  bar(g, x0, y0, x1, y0, w);
  bar(g, x1, y0, x1, y1, w);
  bar(g, x1, y1, x0, y1, w);
  bar(g, x0, y1, x0, y0, w);
}

/** A polygon for the +y side, and the same polygon with every y negated. */
function mirror(g: MarkGraphics, p: readonly number[]): void {
  for (const s of [1, -1]) {
    const m = [...p];
    for (let i = 1; i < m.length; i += 2) m[i] *= s;
    poly(g, m);
  }
}

/** A bar on each side of the hull axis. */
function mbar(
  g: MarkGraphics, x0: number, y0: number, x1: number, y1: number, w: number,
): void {
  bar(g, x0, y0, x1, y1, w);
  bar(g, x0, -y0, x1, -y1, w);
}

/** A dot on each side of the hull axis. */
function mdot(g: MarkGraphics, x: number, y: number, r: number): void {
  dot(g, x, y, r);
  dot(g, x, -y, r);
}

/** A mini dart or a mini needle at (x,y), scaled by `s`, heading `a`. */
function mini(
  g: MarkGraphics, x: number, y: number, s: number, a: number, kind?: 'needle',
): void {
  const p = kind === 'needle'
    ? [13, 0, -6, 1.6, -4.6, 0, -6, -1.6]
    : [14, 0, -8, 5, -5, 0, -8, -5];
  const ca = Math.cos(a), sa = Math.sin(a);
  const q: number[] = [];
  for (let i = 0; i < p.length; i += 2) {
    q.push(x + (p[i] * ca - p[i + 1] * sa) * s, y + (p[i] * sa + p[i + 1] * ca) * s);
  }
  poly(g, q);
}

/** A chevron: an apex at (x,0) and two arms of length `L` at angle `k`. */
function chev(
  g: MarkGraphics, x: number, dir: number, L: number, w: number, k: number,
): void {
  mbar(g, x, 0, x - dir * Math.cos(k) * L, Math.sin(k) * L, w);
}

/** A rung: a bar across the hull at x, half length `L`, sweep `k` per unit y. */
function rung(g: MarkGraphics, x: number, L: number, w: number, k: number): void {
  mbar(g, x, 0, x + k * L, L, w);
}

/**
 * Teeth on the flank of a base whose half width at x is `edge(x)`: a base of
 * 2·`b`, a height `h`, and a tip swept by `sw`.
 */
function teeth(
  g: MarkGraphics, edge: (x: number) => number, xs: readonly number[],
  b: number, h: number, sw: number,
): void {
  for (const x of xs) {
    const e = edge(x);
    mirror(g, [x - b, e - 0.2, x + b, e - 0.2, x + sw, e + h]);
  }
}

/** The half width of the combat dart at x. */
const edgeDart = (x: number): number => (5 * (14 - x)) / 22;
/** The half width of the support needle at x. */
const edgeSup = (x: number): number => (1.6 * (13 - x)) / 19;

/** One tooth on one flank of the support needle: `s` = 1 starboard, -1 port
 *  (round 067). */
function tooth(g: MarkGraphics, x: number, b: number, h: number, sw: number, s: number): void {
  const e = edgeSup(x);
  poly(g, [x - b, s * (e - 0.2), x + b, s * (e - 0.2), x + sw, s * (e + h)]);
}

/** A polyline of bars with a round joint at each bend, so a bent line stays
 *  whole (round 067). */
function chain(g: MarkGraphics, pts: readonly (readonly [number, number])[], w: number): void {
  for (let i = 1; i < pts.length; i++) bar(g, pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1], w);
  for (let i = 1; i < pts.length - 1; i++) dot(g, pts[i][0], pts[i][1], w / 2);
}

/** A solid star of five points at (x,y), the first point at angle `rot`. */
function star(g: MarkGraphics, x: number, y: number, ro: number, ri: number, rot: number): void {
  poly(g, starPoints(5, ro, ri, rot).map((v, k) => v + (k % 2 === 0 ? x : y)));
}

/** A point at bearing `b` (degrees, + to starboard) and range `r` from (x0,0). */
function at(x0: number, b: number, r: number): [number, number] {
  const a = (b * PI) / 180;
  return [x0 + Math.cos(a) * r, Math.sin(a) * r];
}

// ---------------------------------------------------------------------------
// The roster
// ---------------------------------------------------------------------------

const C = ShipClass;

/** The seven combat classes, in roster order. The Commodore, the command
 *  ship, stands seventh, as on the page of round 108 (R2550). */
export const COMBAT_CLASSES: readonly ShipClass[] = [
  C.SHIP_CLASS_CORSAIR, C.SHIP_CLASS_CUTLASS, C.SHIP_CLASS_FALCONET,
  C.SHIP_CLASS_BASILISK, C.SHIP_CLASS_BOMBARD, C.SHIP_CLASS_LANCER,
  C.SHIP_CLASS_COMMODORE,
];

/** The seven support classes, in roster order. The Sapper stands seventh,
 *  as on the page of round 067 (R2652). */
export const SUPPORT_CLASSES: readonly ShipClass[] = [
  C.SHIP_CLASS_SENTINEL, C.SHIP_CLASS_VEDETTE, C.SHIP_CLASS_ORACLE,
  C.SHIP_CLASS_TENDER, C.SHIP_CLASS_BEACON, C.SHIP_CLASS_DREDGER,
  C.SHIP_CLASS_SAPPER,
];

/**
 * The hull field of each class (R835): the combat classes, then the
 * support classes, in the roster order of this file. The Commodore's field
 * `shipCommodore` landed with the marks of R2550, and the Sapper's field
 * `shipSapper` with the marks of round 067 (R2652).
 */
export const HULL_FIELD_OF_CLASS: ReadonlyMap<ShipClass, MarksHullField> = new Map([
  [C.SHIP_CLASS_CORSAIR, 'shipCorsair'],
  [C.SHIP_CLASS_CUTLASS, 'shipCutlass'],
  [C.SHIP_CLASS_FALCONET, 'shipFalconet'],
  [C.SHIP_CLASS_BASILISK, 'shipBasilisk'],
  [C.SHIP_CLASS_BOMBARD, 'shipBombard'],
  [C.SHIP_CLASS_LANCER, 'shipLancer'],
  [C.SHIP_CLASS_COMMODORE, 'shipCommodore'],
  [C.SHIP_CLASS_SENTINEL, 'shipSentinel'],
  [C.SHIP_CLASS_VEDETTE, 'shipVedette'],
  [C.SHIP_CLASS_ORACLE, 'shipOracle'],
  [C.SHIP_CLASS_TENDER, 'shipTender'],
  [C.SHIP_CLASS_BEACON, 'shipBeacon'],
  [C.SHIP_CLASS_DREDGER, 'shipDredger'],
  [C.SHIP_CLASS_SAPPER, 'shipSapper'],
] as [ShipClass, MarksHullField][]);

/** Is this one of the seven support classes? */
const isSupportClass = (cls: ShipClass): boolean => SUPPORT_CLASSES.includes(cls);

/** What a combat mark says about the way the hull fights. */
type Role = 'skirmish' | 'brawler' | 'artillery' | 'torpedo';

const ROLE = new Map<ShipClass, Role>([
  [C.SHIP_CLASS_CORSAIR, 'skirmish'],
  [C.SHIP_CLASS_CUTLASS, 'brawler'],
  [C.SHIP_CLASS_FALCONET, 'artillery'],
  [C.SHIP_CLASS_BASILISK, 'artillery'],
  [C.SHIP_CLASS_BOMBARD, 'brawler'],
  [C.SHIP_CLASS_LANCER, 'torpedo'],
]);

/** The weight step of a combat class: 1 light, 2 medium, 3 heavy. */
const TIER = new Map<ShipClass, number>([
  [C.SHIP_CLASS_CORSAIR, 1],
  [C.SHIP_CLASS_CUTLASS, 1],
  [C.SHIP_CLASS_FALCONET, 2],
  [C.SHIP_CLASS_BASILISK, 3],
  [C.SHIP_CLASS_BOMBARD, 3],
  [C.SHIP_CLASS_LANCER, 2],
]);

const roleOf = (cls: ShipClass): Role => ROLE.get(cls) ?? 'skirmish';
const tierOf = (cls: ShipClass): number => TIER.get(cls) ?? 1;

/**
 * A view of a surface whose polygons are wound to ADD.
 *
 * The card-01 extras live in `marks.ts`, and its own `bar()` states the four
 * corners of a quad in a fixed order rather than by area. A set that keeps one
 * of those extras draws it through this view, so every polygon of every
 * alternative set obeys the winding rule of this file. Nothing else changes:
 * a shape that overlaps nothing fills the same either way.
 */
class Oriented implements MarkGraphics {
  constructor(private readonly g: MarkGraphics) {}
  moveTo(x: number, y: number) { this.g.moveTo(x, y); return this; }
  lineTo(x: number, y: number) { this.g.lineTo(x, y); return this; }
  arc(x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean) {
    this.g.arc(x, y, r, a0, a1, ccw);
    return this;
  }
  closePath() { this.g.closePath(); return this; }
  poly(points: number[]) { poly(this.g, points); return this; }
  circle(x: number, y: number, r: number) { this.g.circle(x, y, r); return this; }
  fill(style: Parameters<MarkGraphics['fill']>[0]) { this.g.fill(style); return this; }
  stroke(style: Parameters<MarkGraphics['stroke']>[0]) { this.g.stroke(style); return this; }
}

/** The card-01 extras of one support class, for a set that keeps one. */
const CARD01 = new Map<ShipClass, HullPath>(SUPPORT_EXTRAS);
const card01 = (g: MarkGraphics, cls: ShipClass): void => {
  CARD01.get(cls)?.(new Oriented(g));
};

// ---------------------------------------------------------------------------
// The eleven alternative sets
// ---------------------------------------------------------------------------

/** The extras one class wears in one set. The base is drawn before it. */
type Extras = (g: MarkGraphics, cls: ShipClass) => void;

/** One set: what the combat row wears, and what the support row wears. */
interface StyleDef {
  combat: Extras;
  support: Extras;
}

const SET_DEFS: Record<Exclude<ShipStyle, 'default'>, StyleDef> = {
  // Card 02 "Rungs": a rung is one bar across the hull. A brawler carries
  // square rungs at the bow, because it closes; artillery carries them
  // astern, because it stands off; the skirmisher's one rung sweeps back and
  // the torpedo boat's sweep forward. The count is the weight.
  rungs: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h), L = [7, 8.5, 10][t - 1], w = 1.7;
      if (r === 'brawler') for (let i = 0; i < t; i++) rung(g, 3 - 4 * i, L, w, 0);
      if (r === 'artillery') for (let i = 0; i < t; i++) rung(g, -4 - 4 * i, L, w, 0);
      if (r === 'skirmish') rung(g, -6, L, w, -0.7);
      if (r === 'torpedo') for (let i = 0; i < t; i++) rung(g, 1 - 4 * i, L, w, 0.7);
    },
    // One thin rung each, with a tip that names the trade.
    support(g, h) {
      const w = 1.0;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: rung(g, -3, 6, w, 0); mdot(g, -3, 6.4, 1.3); break;
        case C.SHIP_CLASS_VEDETTE: rung(g, -4, 4, w, -0.7); break;
        case C.SHIP_CLASS_ORACLE: rung(g, 16, 5, w, 0); break;
        case C.SHIP_CLASS_TENDER: rung(g, -3, 6, w, 0); ring(g, -3, 0, 3.6, 2.6); break;
        case C.SHIP_CLASS_BEACON:
          rung(g, -4, 5.5, w, 0); rung(g, -4, 4, w, 1); rung(g, -4, 4, w, -1);
          break;
        case C.SHIP_CLASS_DREDGER:
          bar(g, -5, 0, -11, 0, w); rung(g, -11, 4, w, 0); mbar(g, -11, 4, -14, 4, w);
          break;
        default: break;
      }
    },
  },

  // Card 03 "Pips": a pip is one solid dot. A brawler carries pairs at the
  // bow beam, the knuckles of a fist; artillery carries pips in a line ahead,
  // the shell in flight; the skirmisher trails one, a wake; the torpedo boat
  // carries two ahead, its tubes.
  pips: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h), R = 1.9;
      if (r === 'brawler') for (let i = 0; i < t; i++) mdot(g, 6 - 4 * i, 5.6 + 0.5 * i, R);
      if (r === 'artillery') for (let i = 0; i < t; i++) dot(g, 17.5 + 3.5 * i, 0, t === 3 ? 2.1 : R);
      if (r === 'skirmish') dot(g, -11.5, 0, R);
      if (r === 'torpedo') mdot(g, 17, 3, R);
    },
    // The support row uses a smaller pip.
    support(g, h) {
      const R = 1.2;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: mdot(g, -4, 3.8, R); dot(g, -9.5, 0, R); break;
        case C.SHIP_CLASS_VEDETTE: dot(g, -9, 0, 1.1); break;
        case C.SHIP_CLASS_ORACLE: dot(g, 16.5, 0, R); mdot(g, 15.5, 3.2, R); break;
        case C.SHIP_CLASS_TENDER:
          for (let i = 0; i < 4; i++) {
            const a = (i * PI) / 2;
            dot(g, -3 + Math.cos(a) * 4, Math.sin(a) * 4, R);
          }
          break;
        case C.SHIP_CLASS_BEACON:
          for (let i = 1; i < 6; i++) {
            const a = (i * PI) / 3;
            dot(g, -4 + Math.cos(a) * 5, Math.sin(a) * 5, R);
          }
          break;
        case C.SHIP_CLASS_DREDGER: dot(g, -9, 0, R); dot(g, -13, 0, R); break;
        default: break;
      }
    },
  },

  // Card 04 "Arcs": an arc is a piece of a circle drawn as a band. Artillery
  // carries the range fan ahead, a brawler the close guard astern, the
  // skirmisher two fins at the beam, the torpedo boat the mouths of its tubes.
  arcs: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h), w = t === 3 ? 1.8 : 1.6;
      if (r === 'artillery') {
        band(g, 4, 0, 14, 14 - w, -0.55, 0.55);
        if (t === 3) band(g, 4, 0, 18, 18 - w, -0.5, 0.5);
      }
      if (r === 'brawler') {
        band(g, -2, 0, 9.5, 9.5 - w, PI - 0.95, PI + 0.95);
        if (t === 3) band(g, -2, 0, 13, 13 - w, PI - 1.0, PI + 1.0);
      }
      if (r === 'skirmish') {
        band(g, -3, 0, 8.5, 8.5 - w, PI / 2 - 0.5, PI / 2 + 0.5);
        band(g, -3, 0, 8.5, 8.5 - w, -PI / 2 - 0.5, -PI / 2 + 0.5);
      }
      if (r === 'torpedo') {
        band(g, 10, 3.4, 6, 6 - w, -0.6, 0.6);
        band(g, 10, -3.4, 6, 6 - w, -0.6, 0.6);
      }
    },
    // The support row draws its arcs at half the stroke.
    support(g, h) {
      const w = 0.9;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: band(g, -5, 0, 5.5, 5.5 - w, 0.6, TAU - 0.6); break;
        case C.SHIP_CLASS_VEDETTE: band(g, -3, 0, 7, 7 - w, PI - 0.5, PI + 0.5); break;
        case C.SHIP_CLASS_ORACLE: card01(g, h); break;
        case C.SHIP_CLASS_TENDER: ring(g, -2, 0, 6, 6 - w - 0.4); break;
        case C.SHIP_CLASS_BEACON:
          for (const a of [PI / 2, -PI / 2, PI]) band(g, -4, 0, 6.5, 6.5 - w, a - 0.55, a + 0.55);
          break;
        case C.SHIP_CLASS_DREDGER: band(g, -9, 0, 4.2, 4.2 - w, PI / 2, (3 * PI) / 2); break;
        default: break;
      }
    },
  },

  // Card 05 "Chevrons": two bars meeting at a point, the rank mark of every
  // navy. A brawler stacks them astern, the drive that pushes it in;
  // artillery stacks them ahead, the shell going out; the skirmisher trails
  // one; the torpedo boat carries two at the beam.
  chevrons: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h), w = 1.6, L = t === 3 ? 5 : 4.2, k = 0.9;
      if (r === 'brawler') for (let i = 0; i < t; i++) chev(g, -9.5 - 3.5 * i, 1, L, w, k);
      if (r === 'artillery') for (let i = 0; i < t; i++) chev(g, 19 + 3.5 * i, 1, L, w, k);
      if (r === 'skirmish') chev(g, -14.5, -1, L, w, k);
      if (r === 'torpedo') {
        for (const s of [1, -1]) {
          bar(g, 17, 3.6 * s, 13.6, 1.2 * s, w);
          bar(g, 17, 3.6 * s, 13.6, 6 * s, w);
        }
      }
    },
    // One thin chevron each, in the shape of the trade.
    support(g, h) {
      const w = 1.0, L = 3.4, k = 0.85;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL:
          chev(g, -8, 1, L, w, k);
          bar(g, -8 - Math.cos(k) * L, -Math.sin(k) * L, -8 - Math.cos(k) * L, Math.sin(k) * L, w);
          break;
        case C.SHIP_CLASS_VEDETTE: chev(g, -11, -1, 3, w, k); break;
        case C.SHIP_CLASS_ORACLE: mbar(g, 15, 0, 18.4, 3.6, w); break;
        case C.SHIP_CLASS_TENDER:
          for (const s of [1, -1]) {
            bar(g, -2, 2.4 * s, -5, 5.6 * s, w);
            bar(g, -2, 2.4 * s, 1, 5.6 * s, w);
          }
          break;
        case C.SHIP_CLASS_BEACON:
          for (const s of [1, -1]) {
            bar(g, -3, 6.2 * s, -6, 3.4 * s, w);
            bar(g, -3, 6.2 * s, 0, 3.4 * s, w);
          }
          chev(g, -10.5, -1, 2.8, w, k);
          break;
        case C.SHIP_CLASS_DREDGER:
          bar(g, -5, 0, -8.8, 0, w);
          chev(g, -12.4, -1, 3.6, w, 1.1);
          bar(g, -12.4 + Math.cos(1.1) * 3.6, -Math.sin(1.1) * 3.6,
            -12.4 + Math.cos(1.1) * 3.6, Math.sin(1.1) * 3.6, w);
          break;
        default: break;
      }
    },
  },

  // Card 06 "Plates": a flat piece beside the hull. One rule runs the roster —
  // a combat hull carries its plate SOLID and a support hull carries the same
  // plate as an OUTLINE.
  plates: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h);
      if (r === 'brawler') {
        if (t === 1) { rect(g, -1, 2.6, 3, 6.6); rect(g, -1, -2.6, 3, -6.6); }
        else { rect(g, -3, 2.6, 4, 9); rect(g, -3, -2.6, 4, -9); }
      }
      if (r === 'artillery') {
        if (t === 2) { rect(g, 2, 5.4, 12, 7); rect(g, 2, -5.4, 12, -7); }
        else { rect(g, 0, 5.6, 16, 7.6); rect(g, 0, -5.6, 16, -7.6); }
      }
      if (r === 'skirmish') mirror(g, [-8, 5, -13, 8, -11, 4.6]);
      if (r === 'torpedo') mirror(g, [10, 7, 6.5, 8.6, -3, 8.6, -3, 5.4, 6.5, 5.4]);
    },
    support(g, h) {
      const w = 0.9;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: box(g, -5, 3, -1, 7, w); box(g, -5, -3, -1, -7, w); break;
        case C.SHIP_CLASS_VEDETTE:
          for (const s of [1, -1]) {
            bar(g, -6, 1.6 * s, -11, 4 * s, w);
            bar(g, -11, 4 * s, -9, 1.6 * s, w);
          }
          break;
        case C.SHIP_CLASS_ORACLE: box(g, 4, 3.4, 14, 5, w); box(g, 4, -3.4, 14, -5, w); break;
        case C.SHIP_CLASS_TENDER: ring(g, -2, 0, 6, 5.1); break;
        case C.SHIP_CLASS_BEACON:
          box(g, -5.5, 4, -2.5, 7, w);
          box(g, -5.5, -4, -2.5, -7, w);
          box(g, -11.5, -1.5, -8.5, 1.5, w);
          break;
        case C.SHIP_CLASS_DREDGER: card01(g, h); break;
        default: break;
      }
    },
  },

  // Card 07 "Tails": the bow is the base and the tail is the class. Every
  // mark is longest astern, so the bow stays the clearest point.
  tails: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h);
      if (r === 'brawler') {
        if (t === 1) rect(g, -13, -3.4, -6, 3.4);
        else rect(g, -16, -4.6, -6, 4.6);
      }
      if (r === 'artillery') {
        if (t === 2) bar(g, -5, 0, -17, 0, 1.8);
        else { bar(g, -5, 0, -21, 0, 2); bar(g, -21, -3.5, -21, 3.5, 2); }
      }
      if (r === 'skirmish') mbar(g, -6, 0, -14, 4.5, 1.6);
      if (r === 'torpedo') mbar(g, -7, 2.4, -15, 2.4, 1.5);
    },
    support(g, h) {
      const w = 1.0;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: bar(g, -5, 0, -11, 0, w); bar(g, -11, -4, -11, 4, w); break;
        case C.SHIP_CLASS_VEDETTE: mbar(g, -6, 0, -10, 2.8, 0.9); break;
        case C.SHIP_CLASS_ORACLE:
          dot(g, -8, 0, 1); dot(g, -11.2, 0, 1); dot(g, -14.4, 0, 1);
          break;
        case C.SHIP_CLASS_TENDER: bar(g, -5, 0, -7, 0, w); ring(g, -10, 0, 3.6, 2.6); break;
        case C.SHIP_CLASS_BEACON:
          dot(g, -7, 0, 1.6);
          for (let i = 0; i < 5; i++) {
            const a = (PI * 2) / 3 + (i * PI) / 6;
            bar(g, -7 + Math.cos(a) * 2.2, Math.sin(a) * 2.2,
              -7 + Math.cos(a) * 5.8, Math.sin(a) * 5.8, w);
          }
          break;
        case C.SHIP_CLASS_DREDGER: bar(g, -5, 0, -9, 0, w); box(g, -12.5, -2.6, -9, 2.6, w); break;
        default: break;
      }
    },
  },

  // Card 09 "Teeth": a small triangle on the flank of the base, so the
  // silhouette is serrated. The sweep is the role and the count is the weight.
  teeth: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h);
      const xs = [4, 0, -4].slice(0, t), b = 1.3, hh = t === 3 ? 4 : 3.2;
      if (r === 'brawler') teeth(g, edgeDart, xs, b, hh, -2.6);
      if (r === 'artillery') teeth(g, edgeDart, xs, b, hh, 0);
      if (r === 'skirmish') teeth(g, edgeDart, [-4], b, 3.5, -3.2);
      if (r === 'torpedo') teeth(g, edgeDart, xs, b, hh, 2.6);
    },
    // Tiny teeth in a shape of the trade's own.
    support(g, h) {
      const b = 0.9, hh = 2.4;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: teeth(g, edgeSup, [0, -3, -6], b, 3, 0); break;
        case C.SHIP_CLASS_VEDETTE: teeth(g, edgeSup, [-4], b, hh, -2.4); break;
        case C.SHIP_CLASS_ORACLE: teeth(g, edgeSup, [10.5], b, 3.2, 2.6); break;
        case C.SHIP_CLASS_TENDER:
          teeth(g, edgeSup, [-1, -5], b, 3, 0);
          mbar(g, -1, edgeSup(-1) + 3, -5, edgeSup(-5) + 3, 0.9);
          break;
        case C.SHIP_CLASS_BEACON:
          teeth(g, edgeSup, [-2, -6], b, 3, 0);
          poly(g, [-6, -1, -6, 1, -11, 0]);
          break;
        case C.SHIP_CLASS_DREDGER: teeth(g, edgeSup, [-2, -4, -6], b, hh, 2); break;
        default: break;
      }
    },
  },

  // Card 10 "Arrows only": everything on the water is an arrow. The combat
  // row carries mini darts and the support row mini needles.
  arrows: {
    combat(g, h) {
      const r = roleOf(h), t = tierOf(h);
      if (r === 'brawler') {
        if (t === 1) { mini(g, -10, 5, 0.42, 0); mini(g, -10, -5, 0.42, 0); }
        else {
          mini(g, -11, 5.5, 0.55, 0); mini(g, -11, -5.5, 0.55, 0); mini(g, -15, 0, 0.42, 0);
        }
      }
      if (r === 'artillery') { mini(g, 20, 0, 0.5, 0); if (t === 3) mini(g, 27, 0, 0.4, 0); }
      if (r === 'skirmish') mini(g, -13, 0, 0.42, PI);
      if (r === 'torpedo') { mini(g, 18, 3, 0.38, 0); mini(g, 18, -3, 0.38, 0); }
    },
    support(g, h) {
      const s = 0.42;
      switch (h) {
        case C.SHIP_CLASS_SENTINEL:
          mini(g, -4, 5, s, PI / 2, 'needle');
          mini(g, -4, -5, s, -PI / 2, 'needle');
          break;
        case C.SHIP_CLASS_VEDETTE: mini(g, -11, 0, s, PI, 'needle'); break;
        case C.SHIP_CLASS_ORACLE: mini(g, 18, 0, s, 0, 'needle'); break;
        case C.SHIP_CLASS_TENDER: mini(g, 1, 5, s, 0, 'needle'); break;
        case C.SHIP_CLASS_BEACON:
          for (const a of [(2 * PI) / 3, PI, (4 * PI) / 3]) {
            mini(g, -4 + Math.cos(a) * 6.5, Math.sin(a) * 6.5, 0.36, a, 'needle');
          }
          break;
        case C.SHIP_CLASS_DREDGER:
          bar(g, -5, 0, -9, 0, 1);
          mini(g, -12, 0, s, 0, 'needle');
          break;
        default: break;
      }
    },
  },

  // Card 11 "By name": each hull draws the thing it is named after. The role
  // kinship is loose: the two guns share a barrel, the two brawlers share
  // nothing but mass.
  'by-name': {
    combat(g, h) {
      switch (h) {
        // A grapnel on a line astern.
        case C.SHIP_CLASS_CORSAIR:
          bar(g, -5, 0, -11, 0, 1.4);
          band(g, -11, 2.5, 3.2, 2.0, -PI / 2, PI / 2 + 0.6);
          break;
        // The dart is the blade: a crossguard, a grip and a pommel astern.
        case C.SHIP_CLASS_CUTLASS:
          bar(g, -4, -6, -4, 6, 1.8);
          bar(g, -5, 0, -10, 0, 2);
          dot(g, -11.2, 0, 1.8);
          break;
        // A short barrel ahead and the two wheels of a carriage gun.
        case C.SHIP_CLASS_FALCONET: bar(g, 13, 0, 19, 0, 1.8); mdot(g, -2, 6, 1.9); break;
        // The long barrel ahead and a serpent's zigzag tail.
        case C.SHIP_CLASS_BASILISK:
          bar(g, 13, 0, 25, 0, 2);
          bar(g, -5, 0, -9, 3, 1.6);
          bar(g, -9, 3, -13, -3, 1.6);
          bar(g, -13, -3, -17, 3, 1.6);
          break;
        // A mortar bowl astern, a solid half disc.
        case C.SHIP_CLASS_BOMBARD:
          g.moveTo(-8, 5.5);
          g.arc(-8, 0, 5.5, PI / 2, (3 * PI) / 2);
          g.closePath();
          break;
        // A long lance ahead with a pennon.
        case C.SHIP_CLASS_LANCER:
          bar(g, 13, 0, 27, 0, 1.2);
          poly(g, [18, 0, 23.5, -3.4, 23.5, 0]);
          break;
        default: break;
      }
    },
    support(g, h) {
      switch (h) {
        // A hollow keep astern with a dot inside.
        case C.SHIP_CLASS_SENTINEL: box(g, -12, -3, -6, 3, 1.0); dot(g, -9, 0, 1); break;
        // A pennant on a mast, one side only.
        case C.SHIP_CLASS_VEDETTE:
          bar(g, -3, 0, -3, -4.6, 0.8);
          poly(g, [-3, -4.8, -3, -1.8, 0.8, -3.3]);
          break;
        // An eye ahead of the bow: a ring with a pupil.
        case C.SHIP_CLASS_ORACLE: ring(g, 17, 0, 3.2, 2.3); dot(g, 17, 0, 1); break;
        // The lifebuoy of R724 and the radial of R727, kept as they are.
        case C.SHIP_CLASS_TENDER: card01(g, h); break;
        case C.SHIP_CLASS_BEACON: card01(g, h); break;
        // A scoop astern on a short tow.
        case C.SHIP_CLASS_DREDGER:
          bar(g, -5, 0, -8, 0, 1);
          band(g, -9, 0, 4.6, 3.6, PI / 2, (3 * PI) / 2);
          break;
        default: break;
      }
    },
  },

  // Card 14 "Insignia": one small mark of the fewest strokes. The kinship is
  // in the grammar of position — a gun carries its mark ahead, the rest
  // astern.
  insignia: {
    combat(g, h) {
      switch (h) {
        case C.SHIP_CLASS_CORSAIR: bar(g, -9, 3, -14, -3, 1.3); break;
        case C.SHIP_CLASS_CUTLASS:
          bar(g, -9, 3, -14, -3, 1.3);
          bar(g, -9, -3, -14, 3, 1.3);
          break;
        case C.SHIP_CLASS_FALCONET: bar(g, 18, -4, 18, 4, 1.5); break;
        case C.SHIP_CLASS_BASILISK:
          bar(g, 18, -4.5, 18, 4.5, 1.5);
          bar(g, 22, -4.5, 22, 4.5, 1.5);
          break;
        case C.SHIP_CLASS_BOMBARD: dot(g, -12, 0, 3.2); break;
        case C.SHIP_CLASS_LANCER: bar(g, 16, 0, 22, 0, 1.2); dot(g, 24, 0, 1.6); break;
        default: break;
      }
    },
    support(g, h) {
      switch (h) {
        case C.SHIP_CLASS_SENTINEL: box(g, -12, -2.5, -7, 2.5, 0.9); break;
        case C.SHIP_CLASS_VEDETTE: bar(g, -7, 0, -12, 0, 0.8); break;
        case C.SHIP_CLASS_ORACLE: ring(g, 18, 0, 3, 2.2); dot(g, 18, 0, 0.9); break;
        case C.SHIP_CLASS_TENDER:
          bar(g, -9.5, -3, -9.5, 3, 0.9);
          bar(g, -12.5, 0, -6.5, 0, 0.9);
          break;
        case C.SHIP_CLASS_BEACON:
          for (const a of [0, PI / 3, (2 * PI) / 3]) {
            bar(g, -9.5 - Math.cos(a) * 3.2, -Math.sin(a) * 3.2,
              -9.5 + Math.cos(a) * 3.2, Math.sin(a) * 3.2, 0.9);
          }
          break;
        case C.SHIP_CLASS_DREDGER: rect(g, -12, -2, -8, 2); break;
        default: break;
      }
    },
  },

  // Card 19 "Filigree": the combat row drops to the stroke of the support row
  // and keeps its rank by count and by the dart underneath. The support row
  // is card 01 unchanged.
  filigree: {
    combat(g, h) {
      switch (h) {
        case C.SHIP_CLASS_CORSAIR: mbar(g, -9, 2.5, -17, 3.2, 0.9); break;
        case C.SHIP_CLASS_CUTLASS:
          mbar(g, 6, 1.8, 6, 6.5, 1.0);
          mbar(g, 2, 2.8, 2, 7, 1.0);
          break;
        case C.SHIP_CLASS_FALCONET:
          bar(g, 13, 0, 21, 0, 1.0);
          bar(g, 21, -2, 21, 2, 0.9);
          break;
        case C.SHIP_CLASS_BASILISK:
          bar(g, 13, 0, 27, 0, 1.0);
          bar(g, 21, -2, 21, 2, 0.9);
          bar(g, 27, -2, 27, 2, 0.9);
          break;
        case C.SHIP_CLASS_BOMBARD: bar(g, 13, 0, 16, 0, 1.0); ring(g, 20.5, 0, 4.5, 3.6); break;
        case C.SHIP_CLASS_LANCER:
          bar(g, 13, 0, 15, 0, 1.0);
          dot(g, 17, 0, 0.9); dot(g, 20, 0, 0.9); dot(g, 23, 0, 0.9);
          break;
        default: break;
      }
    },
    support: card01,
  },
};

// ---------------------------------------------------------------------------
// The command ship in each set (R2550)
// ---------------------------------------------------------------------------

/**
 * The extras of the Commodore in each alternative set: the card of round
 * 108 that the owner picked for the set on 2026-10-19 (v2-the-deep.md
 * §16.7), with the paths of the round page. The dart is drawn before them.
 * The AUTO set draws card 03 with the star of card 02, in `marks.ts`.
 *
 * The role and the weight of the set grammar already say how the other
 * hulls fight, so each card states the one flagship that leads the group:
 * a flag, the formation stations, the aura, the group, a star, the call of
 * MUSTER or the helm.
 */
const COMMAND_EXTRAS: Record<Exclude<ShipStyle, 'default'>, (g: MarkGraphics) => void> = {
  // Card 07 Yard Flags: one long rung amidships, the yard, and a small
  // swallow-tailed flag that flies aft from each end.
  rungs(g) {
    rung(g, 1, 10.6, 1.7, 0);
    mirror(g, [0.4, 7.6, -5, 7.6, -3.3, 9.1, -5, 10.6, 0.4, 10.6]);
  },
  // Card 11 Pip Stations: five pips on a circle round the hull, the
  // formation stations.
  pips(g) {
    for (const b of [50, -50, 120, -120, 180]) {
      const [x, y] = at(-1, b, 12.8);
      dot(g, x, y, 1.6);
    }
  },
  // Card 13 Full Halo: one closed ring round the hull at the support
  // stroke, the aura (R2550 Open: the eye test rules on the ring).
  arcs(g) { ring(g, -1, 0, 12.8, 12.0); },
  // Card 18 Beam Chevrons: one chevron at each beam, its point outboard.
  chevrons(g) {
    for (const s of [1, -1]) {
      bar(g, -1, 10.5 * s, -4, 7.3 * s, 1.6);
      bar(g, -1, 10.5 * s, 2, 7.3 * s, 1.6);
    }
  },
  // Card 20 Escort Plates: four small square plates detached round the
  // hull on the diagonals, the escorts on their stations.
  plates(g) {
    for (const b of [45, -45, 135, -135]) {
      const [x, y] = at(-1, b, 12.5);
      rect(g, x - 1.7, y - 1.7, x + 1.7, y + 1.7);
    }
  },
  // Card 23 Star Tail: a short tow astern that ends in a solid star.
  tails(g) {
    bar(g, -5, 0, -10, 0, 1.6);
    star(g, -14, 0, 4.2, 1.8, PI);
  },
  // Card 27 Star Teeth: one tall square tooth on each flank amidships and
  // one on the axis astern, so the mark is a star of four points.
  teeth(g) {
    const e = edgeDart(-1);
    mirror(g, [-2.6, e - 0.2, 0.6, e - 0.2, -1, e + 5.6]);
    poly(g, [-5.4, -1.5, -5.4, 1.5, -11.4, 0]);
  },
  // Card 29 Muster Minis: four mini darts on the diagonals that point in
  // at the hull, the hulls that MUSTER calls in.
  arrows(g) {
    for (const b of [45, -45, 135, -135]) {
      const [x, y] = at(-1, b, 15);
      mini(g, x, y, 0.36, (b * PI) / 180 + PI);
    }
  },
  // Card 33 Helm: a ship's wheel astern on a short tow, a rim, a hub and
  // eight handles. Helmsmanship is the lead attribute of the class.
  'by-name'(g) {
    bar(g, -5, 0, -9, 0, 1.0);
    ring(g, -12.8, 0, 3.9, 3.0);
    dot(g, -12.8, 0, 1.0);
    for (let i = 0; i < 8; i++) {
      const a = PI / 8 + (i * PI) / 4;
      bar(g, -12.8 + Math.cos(a) * 3.7, Math.sin(a) * 3.7,
        -12.8 + Math.cos(a) * 5.6, Math.sin(a) * 5.6, 0.9);
    }
  },
  // Card 34 Star: one solid five-point star astern, the rank mark of a
  // flag officer, where the Bombard's dot stands.
  insignia(g) { star(g, -12.5, 0, 4.2, 1.75, 0); },
  // Card 38 Hairline Halo: one hairline ring round the whole hull, the
  // aura (R2550 Open: it breaks the ink rule of the filigree row).
  filigree(g) { ring(g, -1, 0, 13.2, 12.3); },
};

// ---------------------------------------------------------------------------
// The Sapper in each set (R2538, R2652)
// ---------------------------------------------------------------------------

/**
 * The extras of the Sapper in each alternative set: the card of round 067
 * that the owner picked for the set on 2026-10-19 (v2-the-deep.md §11.8),
 * with the paths of the round page. The needle is drawn before them. The
 * AUTO set draws card 01 Mine Rails, in `marks.ts`.
 *
 * Each card states one trade of the military engineer: it LAYS, it STOWS,
 * it COLLECTS, or it draws the tool the trade is named after.
 */
const SAPPER_EXTRAS: Record<Exclude<ShipStyle, 'default'>, (g: MarkGraphics) => void> = {
  // Card 04 Rack: two short plain rungs across the hull, the rack of the
  // stow. Every other support mark of the set carries one rung with a tip.
  rungs(g) { rung(g, 3.4, 4.0, 1.15, 0); rung(g, -3.4, 4.2, 1.15, 0); },
  // Card 07 Stow Rows: two rows of three small pips along the needle, the
  // sea mines on the deck of a minelayer.
  pips(g) { for (const x of [3.6, 0, -3.6]) mdot(g, x, 2.7, 0.95); },
  // Card 12 Waist Brackets: two arcs that bow in toward the needle
  // amidships, the brackets of the stow. Every other arc of the set is
  // convex outward.
  arcs(g) {
    band(g, -1.5, 8.6, 5.6, 4.6, -PI / 2 - 0.78, -PI / 2 + 0.78);
    band(g, -1.5, -8.6, 5.6, 4.6, PI / 2 - 0.78, PI / 2 + 0.78);
  },
  // Card 14 Deck Chevrons: two chevrons on the needle that point aft, the
  // way the sea mines go out over the stern.
  chevrons(g) { chev(g, 0.6, -1, 4.3, 1.05, 0.98); chev(g, -4.4, -1, 4.3, 1.05, 0.98); },
  // Card 16 Stow Box: a hollow box round the needle amidships, the stow of
  // sea mines and deployables. The Tender's ring stands in the same place.
  plates(g) { box(g, -6.4, -2.9, 2.6, 2.9, 0.9); },
  // Card 20 Anchor: the tail is an anchor, a stock at the stern, a shank
  // and a crown. A deployable stands on an anchor, and the Sapper sets it.
  tails(g) {
    bar(g, -5, 0, -13.2, 0, 1.0);
    bar(g, -6.8, -1.9, -6.8, 1.9, 1.0);
    band(g, -9.4, 0, 4.2, 3.2, PI - 1.2, PI + 1.2);
  },
  // Card 22 Side Rake: three teeth on the starboard flank that sweep aft,
  // a rake on one side that gathers sea mines alongside.
  teeth(g) { for (const x of [3.5, 0, -3.5]) tooth(g, x, 1.05, 3.4, -2.2, 1); },
  // Card 25 Inbound Pair: two mini needles astern that point in at the
  // stern, sea mines that come aboard.
  arrows(g) {
    mini(g, -11.4, 4.9, 0.42, -0.66, 'needle');
    mini(g, -11.4, -4.9, 0.42, 0.66, 'needle');
  },
  // Card 28 Spade: a solid spade blade astern on a short haft. A sapper
  // digs, and the spade is his tool.
  'by-name'(g) {
    bar(g, -4.8, 0, -8.4, 0, 1.2);
    poly(g, [-8, 2.5, -11.4, 2.5, -13.6, 0, -11.4, -2.5, -8, -2.5]);
  },
  // Card 33 Open Vee: a V astern with its point aft, the drop in two
  // strokes. The other marks of the set are closed figures.
  insignia(g) { mbar(g, -13, 0, -7.8, 3.3, 0.95); },
  // Card 36 Hairline Claw: two hairline prongs leave the stern, open out
  // and close again behind it, a claw that grips a sea mine.
  filigree(g) {
    for (const s of [1, -1]) chain(g, [[-4.6, 0.9 * s], [-8.4, 4.7 * s], [-12.8, 3.8 * s], [-13.5, 2.1 * s]], 1.0);
  },
};

// ---------------------------------------------------------------------------
// The lookup
// ---------------------------------------------------------------------------

/** The AUTO paths, the ones `marks.ts` holds (card 01). */
const DEFAULT_PATHS = new Map<ShipClass, HullPath>(
  [...SUPPORT_ICONS, ...COMBAT_ICONS] as [ShipClass, HullPath][],
);

/**
 * The path of one class in one set (R835).
 *
 * The `default` set answers the paths of `marks.ts`. Every other set answers
 * the base of the class plus the extras of the set. A class this build does
 * not know wears the dart, which is what a contact with the class withheld
 * wears (R426).
 */
export function hullPathOf(style: ShipStyle, cls: ShipClass): HullPath {
  const def = style === DEFAULT_SHIP_STYLE
    ? undefined : SET_DEFS[style as Exclude<ShipStyle, 'default'>];
  if (def === undefined) return DEFAULT_PATHS.get(cls) ?? dartPath;
  if (!HULL_FIELD_OF_CLASS.has(cls)) return dartPath;
  if (cls === C.SHIP_CLASS_COMMODORE) {
    const extras = COMMAND_EXTRAS[style as Exclude<ShipStyle, 'default'>];
    return (g) => { dartPath(g); extras(g); };
  }
  if (cls === C.SHIP_CLASS_SAPPER) {
    const extras = SAPPER_EXTRAS[style as Exclude<ShipStyle, 'default'>];
    return (g) => { supportBase(g); extras(g); };
  }
  if (isSupportClass(cls)) {
    return (g) => { supportBase(g); def.support(g, cls); };
  }
  return (g) => { dartPath(g); def.combat(g, cls); };
}

/**
 * The set one hull wears under a look (R835): the hull field of the class
 * when it is not `follow`, and the `ship` field of the look when it is.
 */
export function shipStyleOf(look: SceneLook, cls: ShipClass): ShipStyle {
  const field = HULL_FIELD_OF_CLASS.get(cls);
  if (field === undefined) return DEFAULT_SHIP_STYLE;
  const v = look[field];
  return v === FOLLOW_HULL_STYLE ? look.ship : (v as ShipStyle);
}
